import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { Badge, Button, DataTable, FilterChips, Modal, PageHeader, type ColumnDef, type FilterChipOption } from '@lipaflow/ui'
import {
  OPERATING_MODEL_LABELS,
  operatingModelsFor,
  verificationService,
  type DemoInviteSummary,
  type InterestType,
  type OperatingModel,
  type CollectionAccountCheck,
  type VerificationDetail,
  type VerificationStatus,
  type VerificationSummary,
} from '../services/admin'
import { usePageTitle } from '../lib/pageTitle'
import { useSector } from '../sector/sector'
import { SignupsPanel } from './home/SignupsPanel'
import { apiErrorMessage } from '@lipaflow/shared'

type View = 'pending' | 'approved' | 'rejected'
type BadgeVariant = 'neutral' | 'success' | 'teal' | 'warning' | 'info'

const PENDING_STATUSES: VerificationStatus[] = ['pending', 'awaiting_contact', 'in_progress']
const STATUSES_FOR: Record<View, VerificationStatus[]> = {
  pending: PENDING_STATUSES,
  approved: ['approved'],
  rejected: ['rejected'],
}

// Status roles only (brand doc §8): declining a lead is a decision, not red.
const STATUS: Record<VerificationStatus, { label: string; variant: BadgeVariant }> = {
  pending: { label: 'New', variant: 'warning' },
  awaiting_contact: { label: 'Trying to reach them', variant: 'warning' },
  in_progress: { label: 'Being checked', variant: 'info' },
  approved: { label: 'Approved', variant: 'success' },
  rejected: { label: 'Declined', variant: 'neutral' },
}
const INTEREST: Record<InterestType, string> = { demo: 'Wants a demo', sales: 'Enterprise / sales', waitlist: 'Waitlist' }
const INVITE_STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Not used yet', variant: 'warning' },
  redeemed: { label: 'Used — account created', variant: 'success' },
  expired: { label: 'Expired', variant: 'neutral' },
  revoked: { label: 'Cancelled', variant: 'neutral' },
}
// `sent` means the provider accepted it; `delivered`/`bounced` arrive by webhook.
const DELIVERY: Record<string, string> = {
  pending: 'Not sent yet',
  sent: 'Sent, waiting for delivery confirmation',
  delivered: 'Delivered',
  bounced: 'Bounced',
  failed: 'Failed to send',
  skipped: 'Not emailed',
}

function ago(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return '—'
  const h = ms / 3_600_000
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m ago`
  if (h < 24) return `${Math.floor(h)}h ago`
  return `${Math.floor(h / 24)}d ago`
}

const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'

/**
 * Verification queue — people who asked for LipaFlow in this sector, checked
 * before they get an account. Approving a new lead emails them a sign-up
 * link; declining records why. Everything happens in one panel per request.
 */
export default function VerificationQueuePage() {
  usePageTitle('Verifications')
  const sector = useSector()
  const [view, setView] = useState<View>('pending')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['admin-verifications', sector.code, view],
    queryFn: () => verificationService.list(sector.code, { status: STATUSES_FOR[view], limit: 50, offset: 0 }),
  })
  const pendingCount = useQuery({
    queryKey: ['admin-verifications', sector.code, 'pending-count'],
    queryFn: () => verificationService.list(sector.code, { status: PENDING_STATUSES, limit: 1, offset: 0 }),
  })

  const views: FilterChipOption<View>[] = [
    { key: 'pending', label: 'To review', count: pendingCount.data?.total, countNeedsAttention: true },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Declined' },
  ]

  const columns: ColumnDef<VerificationSummary>[] = [
    {
      key: 'who',
      header: 'Organisation',
      cell: (v) => (
        <div>
          <div className="font-medium text-ink">{v.institution_name || 'Name not given'}</div>
          <div className="text-caption text-ink-muted">{v.contact_name} · {v.county || 'County not given'}</div>
        </div>
      ),
    },
    {
      key: 'what',
      header: 'Looking for',
      hideOnMobile: true,
      cell: (v) => (
        <div className="text-body-sm text-ink-muted">
          {v.tenant_id ? 'To go live' : v.interest_type ? INTEREST[v.interest_type] : '—'}
          {v.estimated_size && <div className="text-caption text-ink-faint">{v.estimated_size}</div>}
        </div>
      ),
    },
    { key: 'when', header: 'Asked', hideOnMobile: true, cell: (v) => <span className="text-caption text-ink-muted">{ago(v.submitted_at)}</span> },
    { key: 'status', header: 'Status', cell: (v) => <Badge variant={STATUS[v.status].variant} size="sm">{STATUS[v.status].label}</Badge> },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Verifications"
        subtitle="Start free sign-ups open on their own when they pass the checks. The ones here need a person."
      />

      <SignupsPanel sector={sector.code} sectorName={sector.name} />

      <FilterChips options={views} value={view} onChange={(v) => { setView(v); setSelectedId(null) }} />

      {list.isError ? (
        <p className="rounded-card bg-warning-soft px-4 py-3 text-body-sm text-ink">Couldn't load requests. Refresh to try again.</p>
      ) : (
        <DataTable
          data={list.data?.items ?? []}
          columns={columns}
          getRowId={(v) => v.id}
          loading={list.isLoading}
          onRowClick={(v) => setSelectedId(v.id)}
          activeRowId={selectedId ?? undefined}
          emptyState={{
            title: view === 'pending' ? 'Nothing to review' : view === 'approved' ? 'Nothing approved yet' : 'Nothing declined',
            description: view === 'pending' ? `New ${sector.name} requests from the website appear here.` : 'Try another view.',
          }}
          mobileCardRenderer={(v) => (
            <button type="button" onClick={() => setSelectedId(v.id)} className="w-full text-left p-4 border-b border-hairline">
              <span className="flex items-center justify-between gap-2">
                <span className="font-medium text-ink truncate">{v.institution_name || 'Name not given'}</span>
                <Badge variant={STATUS[v.status].variant} size="sm">{STATUS[v.status].label}</Badge>
              </span>
              <span className="block text-caption text-ink-muted">{v.contact_name} · {ago(v.submitted_at)}</span>
            </button>
          )}
        />
      )}

      <VerificationPanel id={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  )
}

function VerificationPanel({ id, onClose }: { id: string | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'view' | 'approve' | 'reject'>('view')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')
  const [model, setModel] = useState<OperatingModel | null>(null)
  const [calledNumber, setCalledNumber] = useState('')
  const [calledSource, setCalledSource] = useState('')

  const detail = useQuery({ queryKey: ['admin-verification-detail', id], queryFn: () => verificationService.get(id!), enabled: !!id })
  const invite = useQuery({
    queryKey: ['admin-verification-invite', id],
    queryFn: () => verificationService.getInvite(id!),
    enabled: !!id && detail.data?.status === 'approved',
  })

  const close = () => { setMode('view'); setNote(''); setReason(''); onClose() }
  const decided = (message: string) => {
    toast.success(message)
    queryClient.invalidateQueries({ queryKey: ['admin-verifications'] })
    queryClient.invalidateQueries({ queryKey: ['admin-verification-detail'] })
    close()
  }

  const approve = useMutation({
    mutationFn: () => {
      // Send the model only when staff changed the prospect's own answer.
      const changed = model !== (detail.data?.operating_model ?? null)
      const call = calledNumber.trim() ? { number: calledNumber.trim(), source: calledSource.trim() } : null
      return verificationService.approve(id!, note.trim() || undefined, changed ? model : null, call)
    },
    onSuccess: () => decided('Approved'),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't approve")),
  })
  const reject = useMutation({
    mutationFn: () => verificationService.reject(id!, reason.trim()),
    onSuccess: () => decided('Declined'),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't decline")),
  })
  const sendInvite = useMutation({
    mutationFn: () => verificationService.sendInvite(id!),
    onSuccess: (inv) => {
      toast.success(inv.send_count > 1 ? 'Sign-up link sent again' : 'Sign-up link sent')
      queryClient.invalidateQueries({ queryKey: ['admin-verification-invite', id] })
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't send the link")),
  })
  const revokeInvite = useMutation({
    mutationFn: (inviteId: string) => verificationService.revokeInvite(inviteId),
    onSuccess: () => {
      toast.success('Sign-up link cancelled')
      queryClient.invalidateQueries({ queryKey: ['admin-verification-invite', id] })
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't cancel the link")),
  })

  const d: VerificationDetail | undefined = detail.data
  const models = operatingModelsFor(d?.vertical_code)

  return (
    <Modal open={!!id} onClose={close} title={d?.institution_name || 'Request'} size="lg">
      {detail.isLoading ? (
        <p role="status" className="text-body-sm text-ink-muted">Loading…</p>
      ) : !d ? (
        <p className="text-body-sm text-warning">Couldn't load this request.</p>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={STATUS[d.status].variant}>{STATUS[d.status].label}</Badge>
            {d.interest_type && <Badge variant="neutral">{INTEREST[d.interest_type]}</Badge>}
            {d.tenant_id === null && <span className="text-caption text-ink-muted">No account yet</span>}
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Fact
              label="Registration number"
              value={d.registration_number || '—'}
              sub={d.tenant_id !== null ? 'Sent from setup, to go live' : undefined}
            />
            {d.has_document && (
              <Fact
                label="Certificate"
                value={
                  <button
                    type="button"
                    className="text-accent hover:underline"
                    onClick={async () => {
                      try {
                        const doc = await verificationService.document(d.id)
                        window.open(doc.url, '_blank', 'noopener')
                      } catch (e) {
                        toast.error(apiErrorMessage(e, "Couldn't open the certificate"))
                      }
                    }}
                  >
                    Open certificate
                  </button>
                }
              />
            )}
            <Fact label="County / town" value={d.county || '—'} />
            <Fact label="Operates as" value={d.operating_model ? OPERATING_MODEL_LABELS[d.operating_model] : 'Not said'} />
            <Fact label="Size" value={d.estimated_size || '—'} />
            <Fact label="Contact" value={d.contact_name} sub={d.contact_title || undefined} />
            <Fact
              label="Reach them"
              value={
                <span className="flex flex-col">
                  {d.contact_email && <a href={`mailto:${d.contact_email}`} className="text-accent hover:underline">{d.contact_email}</a>}
                  {d.contact_phone && <a href={`tel:${d.contact_phone}`} className="text-accent hover:underline">{d.contact_phone}</a>}
                </span>
              }
            />
            <Fact label="Asked" value={d.submitted_at ? new Date(d.submitted_at).toLocaleString('en-KE') : '—'} sub={d.lead_source || undefined} />
          </dl>

          {d.collection_account && <AccountCheck check={d.collection_account} />}

          {d.lead_notes && (
            <div className="p-4 rounded-card bg-surface-alt">
              <p className="text-caption text-ink-faint mb-1">What they told us</p>
              <p className="text-body-sm text-ink whitespace-pre-wrap">{d.lead_notes}</p>
            </div>
          )}

          {d.lead_attribution && Object.values(d.lead_attribution).some(Boolean) && (
            <details className="rounded-card border border-hairline">
              <summary className="cursor-pointer px-4 py-3 text-body-sm font-medium text-ink">Where they came from</summary>
              <dl className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(d.lead_attribution).filter(([, v]) => v).map(([k, v]) => (
                  <Fact key={k} label={k.replace(/_/g, ' ')} value={<span className="font-mono text-caption break-all">{String(v)}</span>} />
                ))}
              </dl>
            </details>
          )}

          {d.rejection_reason && (
            <p className="text-body-sm text-ink-muted"><span className="font-medium text-ink">Declined because:</span> {d.rejection_reason}</p>
          )}

          {d.status === 'approved' && (
            <InviteSection
              invite={invite.data ?? null}
              loading={invite.isLoading}
              sending={sendInvite.isPending}
              revoking={revokeInvite.isPending}
              onSend={() => sendInvite.mutate()}
              onRevoke={(inviteId) => revokeInvite.mutate(inviteId)}
            />
          )}

          {PENDING_STATUSES.includes(d.status) && mode === 'view' && (
            <div className="flex flex-wrap gap-2 pt-4 border-t border-hairline">
              <Button onClick={() => { setModel(d.operating_model ?? null); setMode('approve') }}>Approve</Button>
              <Button variant="secondary" onClick={() => setMode('reject')}>Decline</Button>
            </div>
          )}

          {mode === 'approve' && (
            <div className="pt-4 border-t border-hairline space-y-3">
              <p className="text-body-sm text-ink">
                {d.tenant_id === null
                  ? 'Approving emails them a sign-up link. Their account is created when they use it.'
                  : 'Approving records your check against their existing account.'}
              </p>
              {/* Only for a lead: an existing account's model is changed on the customer. */}
              {d.tenant_id === null && models.length > 1 && (
                <div>
                  <label htmlFor="approve-model" className="block text-caption text-ink-muted mb-1">Operates as</label>
                  <select id="approve-model" value={model ?? ''} onChange={(e) => setModel((e.target.value || null) as OperatingModel | null)} className={field}>
                    {model === null && <option value="">Not set (treated as owner)</option>}
                    {models.map((m) => <option key={m} value={m}>{OPERATING_MODEL_LABELS[m]}</option>)}
                  </select>
                  <p className="text-caption text-ink-muted mt-1">
                    What they told us. Change it if your checks show otherwise: an agency gets trust accounting and two-person payout approval.
                  </p>
                </div>
              )}
              {accountBlocker(d.collection_account) && (
                <p role="alert" className="rounded-card bg-warning-soft p-3 text-body-sm text-ink">{accountBlocker(d.collection_account)}</p>
              )}
              {d.collection_account?.needs_call && !accountBlocker(d.collection_account) && (
                <div className="rounded-card bg-surface-alt p-3 space-y-3">
                  <p className="text-body-sm text-ink">
                    {d.collection_account.collects
                      ? "The bank account isn't registered in their name, so call them first."
                      : 'They collect no money through LipaFlow, so confirm them with a call.'}{' '}
                    Use a number <strong>you found</strong> (MoE list, their website, a public listing), never one they gave us.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="called-number" className="block text-caption text-ink-muted mb-1">Number you called</label>
                      <input id="called-number" type="tel" value={calledNumber} onChange={(e) => setCalledNumber(e.target.value)} className={field} />
                    </div>
                    <div>
                      <label htmlFor="called-source" className="block text-caption text-ink-muted mb-1">Where you found it</label>
                      <input id="called-source" value={calledSource} onChange={(e) => setCalledSource(e.target.value)} placeholder="e.g. school website contact page" className={field} />
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="approve-note" className="block text-caption text-ink-muted mb-1">Internal note (optional)</label>
                <textarea id="approve-note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Checked the Ministry of Education listing and called the bursar" className={field} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setMode('view')}>Back</Button>
                <Button
                  onClick={() => approve.mutate()}
                  isLoading={approve.isPending}
                  disabled={
                    Boolean(accountBlocker(d.collection_account)) ||
                    (Boolean(d.collection_account?.needs_call) && (calledNumber.replace(/\D/g, '').length < 7 || calledSource.trim().length < 3))
                  }
                >
                  Approve
                </Button>
              </div>
            </div>
          )}

          {mode === 'reject' && (
            <div className="pt-4 border-t border-hairline space-y-2">
              <label htmlFor="reject-reason" className="block text-body-sm font-medium text-ink">
                Why are you declining? This is kept in the activity log and may be used when we reply to them.
              </label>
              <textarea
                id="reject-reason"
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Couldn't confirm the school's registration; the phone number is disconnected"
                className={field}
              />
              <p className="text-caption text-ink-muted">At least 5 characters · {reason.length}/500</p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setMode('view')}>Back</Button>
                <Button variant="secondary" disabled={reason.trim().length < 5} onClick={() => reject.mutate()} isLoading={reject.isPending}>
                  Decline
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

/** What must happen before an organisation that collects money can be approved. */
function accountBlocker(check: CollectionAccountCheck | null | undefined): string | null {
  if (!check?.collects) return null
  if (!check.connected) return 'Connect their collection account first (Payment connections), recording who the bank says it is registered to. Then approve.'
  if (!check.holder_name) return "Record who the account is registered to, as the bank confirmed it, on their connection first."
  return null
}

/** The account approval is checked against: registered to them, as the bank confirmed. */
function AccountCheck({ check }: { check: CollectionAccountCheck }) {
  if (!check.collects) {
    return (
      <section className="rounded-card border border-hairline p-4">
        <p className="text-body-sm font-medium text-ink">No collection account</p>
        <p className="text-caption text-ink-muted mt-1">They collect no money through LipaFlow (CBC only). Approve after an independent call.</p>
      </section>
    )
  }
  return (
    <section className="rounded-card border border-hairline p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-body-sm font-medium text-ink">Collection account</p>
        {!check.connected ? (
          <Badge variant="warning" size="sm">Not connected yet</Badge>
        ) : check.names_match ? (
          <Badge variant="success" size="sm">Registered to them</Badge>
        ) : (
          <Badge variant="warning" size="sm">{check.holder_name ? 'Different name' : 'Holder not recorded'}</Badge>
        )}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Fact label="Account" value={[check.bank, check.number].filter(Boolean).join(' · ') || '—'} />
        <Fact label="Registered to (bank-confirmed)" value={check.holder_name || 'Not recorded'} sub={`Organisation: ${check.organisation_name}`} />
        {check.requested_name && <Fact label="Name they gave" value={check.requested_name} />}
      </dl>
      {!check.connected && (
        <p className="text-caption text-ink-muted">Connect it in Integrations, recording who the bank says it is registered to, then approve.</p>
      )}
    </section>
  )
}

function InviteSection({
  invite,
  loading,
  sending,
  revoking,
  onSend,
  onRevoke,
}: {
  invite: DemoInviteSummary | null
  loading: boolean
  sending: boolean
  revoking: boolean
  onSend: () => void
  onRevoke: (inviteId: string) => void
}) {
  if (loading) return <p role="status" className="text-body-sm text-ink-muted">Checking the sign-up link…</p>

  if (!invite) {
    // Approval sends the link automatically for a new lead with an email, so
    // reaching here means there was no email or the send failed.
    return (
      <section className="rounded-card bg-warning-soft p-4 space-y-2">
        <p className="flex items-center gap-2 text-body-sm font-medium text-ink"><EnvelopeIcon className="h-4 w-4" /> No sign-up link has gone out</p>
        <p className="text-caption text-ink-muted">Either there was no email address or sending failed when this was approved.</p>
        <div className="flex justify-end">
          <Button size="sm" onClick={onSend} isLoading={sending}>Send sign-up link</Button>
        </div>
      </section>
    )
  }

  const status = INVITE_STATUS[invite.status] ?? INVITE_STATUS.pending
  const deliveryBad = invite.delivery_status === 'failed' || invite.delivery_status === 'bounced'
  return (
    <section className="rounded-card border border-hairline p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-body-sm font-medium text-ink"><EnvelopeIcon className="h-4 w-4 text-accent" /> Sign-up link</p>
        <Badge variant={status.variant} size="sm">{status.label}</Badge>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Fact label="Sent to" value={invite.email} />
        <Fact label="Last sent" value={new Date(invite.last_sent_at).toLocaleString('en-KE')} sub={`${invite.send_count} ${invite.send_count === 1 ? 'time' : 'times'}`} />
        <Fact label="Expires" value={new Date(invite.expires_at).toLocaleString('en-KE')} />
        <Fact
          label="Email delivery"
          value={<span className={clsx(deliveryBad && 'text-warning font-medium')}>{DELIVERY[invite.delivery_status] ?? invite.delivery_status}</span>}
          sub={deliveryBad && invite.delivery_error ? invite.delivery_error : undefined}
        />
        {invite.status === 'redeemed' && invite.redeemed_at && (
          <Fact label="Used" value={new Date(invite.redeemed_at).toLocaleString('en-KE')} />
        )}
      </dl>
      {invite.status !== 'redeemed' && (
        <div className="flex flex-wrap justify-end gap-2">
          {invite.status === 'pending' && (
            <Button size="sm" variant="ghost" onClick={() => onRevoke(invite.id)} isLoading={revoking}>Cancel link</Button>
          )}
          <Button size="sm" variant="secondary" onClick={onSend} isLoading={sending}>
            {invite.status === 'pending' ? 'Send again' : 'Send a new link'}
          </Button>
        </div>
      )}
    </section>
  )
}

function Fact({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div>
      <dt className="text-caption text-ink-faint capitalize">{label}</dt>
      <dd className="text-body-sm text-ink mt-0.5">{value}</dd>
      {sub && <dd className="text-caption text-ink-muted">{sub}</dd>}
    </div>
  )
}
